// shared/sharedBootstrapProvider.tsx
'use client';
import {
  Env,
  EventInMemoryStore,
  getDistinctId,
  getUserDeviceId,
  getUserDeviceIdFingerprint,
  posthog,
  useEventStore,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import { adUnits } from '@ninjacart/nc-nk-core';
import { useFF4JStore } from '@ninjacart/nc-nk-ff4j';
import { crashlytics, logEvent, requestNotificationPermission } from '@ninjacart/nc-nk-firebase';
import {
  changeLanguage,
  getStreamUserDetails,
  languageStateCode,
  useAppPersistenceEdTechStore,
  useAppPersistenceStore,
  useEventZStore,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import ReactNativeIdfaAaid from '@sparkfabrik/react-native-idfa-aaid';
import Bowser from 'bowser';
import React, { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { isWeb } from 'tamagui';
import {
  checkInstallReferrer,
  initializeClarity,
  initializeFacebookSDK,
  setupNativeBootstrap,
} from './sharedBootstrapUtils';

interface BootstrapProviderProps {
  children: React.ReactNode;
  initialBootstrapData?: any;
  initialIPLocation?: any;
  refCode?: string | string[];
  onReady?: () => void;
  onError?: (error: Error) => void;
  deviceId?: string;
}

export const SharedBootstrapProvider = ({
  children,
  initialBootstrapData,
  initialIPLocation,
  onError,
  deviceId,
}: BootstrapProviderProps) => {
  const existingLocationDetails = useProfileStore.getState()?.ipAddressLocation;
  const iplocationDetailsRef = useRef(initialIPLocation ?? existingLocationDetails);
  const [isReady, setIsReady] = useState(false);
  // Store hooks
  const setAndroidPlatform = useEventZStore((state) => state.setAndroidPlatform);
  const setIOSPlatform = useEventZStore((state) => state.setIOSPlatform);
  const setTrueCallerEnabled = useEventZStore((state) => state.setTrueCallerEnabled);
  const setGlobalSearchEnabled = useEventZStore((state) => state.setGlobalSearchEnabled);
  const setMandiNewsEnabled = useEventZStore((state) => state.setMandiNewsEnabled);
  const setHomeHeaderEnabled = useEventZStore((state) => state.setHomeHeaderEnabled);
  const setNewUserLoginRedirectPath = useEventZStore((state) => state.setNewUserLoginRedirectPath);
  const setPlayListTitle = useEventZStore((state) => state.setPlayListTitle);
  const setLanguages = useEventZStore((state) => state.setLanguages);
  const setAutoVideoPlayer = useEventZStore((state) => state.setAutoVideoPlayer);
  const setIsVideoplayerAdEnable = useEventZStore((state) => state?.setIsVideoplayerAdEnable);
  const setContactUsWhatsappNumber = useEventZStore((state) => state.setContactUsWhatsappNumber);
  const setBannerAdKeywords = useEventZStore((state) => state.setBannerAdKeywords);
  const setShortsAdFrequency = useEventZStore((state) => state.setShortsAdFrequency);
  const setShortsAdId = useEventZStore((state) => state.setShortsAdId);
  const setShowHotFixPopup = useEventZStore((state) => state.setShowHotFixPopup);
  const setIpAddressLocation = useProfileStore((state) => state.setIpAddressLocation);
  const setAppLanguage = useAppPersistenceStore((state) => state.setAppLanguage);
  const setAppLanguageLabel = useAppPersistenceStore((state) => state.setAppLanguageLabel);
  const setIsActiveTourGuides = useEventZStore((state) => state.setIsActiveTourGuides);
  const setAdSizes = useAppPersistenceStore((state) => state.setAdSizes);
  const setAdSizeWeightage = useAppPersistenceStore((state) => state.setAdSizeWeightage);
  const setWeatherUnitId = useEventZStore((state) => state.setWeatherUnitId);
  const setEdTechAppLanguageLabel = useAppPersistenceEdTechStore(
    (state) => state.setEdTechAppLanguageLabel
  );
  const setProfileTabLanguageFilter = useEventZStore((state) => state.setProfileTabLanguageFilter);
  const setDeviceId = useAppPersistenceStore((state) => state.setDeviceId);
  const setAdTimerShow = useEventZStore((state) => state.setAdTimerShow);
  const userProfile = useProfileStore((state) => state.userProfile);
  const startTimeRef = useRef(performance.now());
  const setAppStartingTime = useEventZStore((state) => state.setAppStartingTime);
  const appLanguage = useAppPersistenceStore.getState()?.appLanguage;
  const setFirstLaunchTime = useAppPersistenceStore((state) => state.setFirstLaunchTime);
  const setEventDetails = useEventStore.getState()?.setEventDetails;
  const setReferrerInfo = useAppPersistenceStore((state) => state.setReferrerInfo);
  const enqueue = useQueuedEventStore.getState()?.enqueue;
  const setAdId = useAppPersistenceStore((state) => state.setAdId);
  const setIsAdTrackingLimited = useAppPersistenceStore((state) => state.setIsAdTrackingLimited);
  const setEnableLanguageIcon = useEventZStore((state) => state.setEnableLanguageIcon);
  const setHomeCustomTabEnabled = useEventZStore((state) => state.setHomeCustomTabEnabled);
  const setEnableGlobalTabMic = useEventZStore((state) => state.setEnableGlobalTabMic);
  const setEnableGlobalProfileTabMic = useEventZStore(
    (state) => state.setEnableGlobalProfileTabMic
  );
  const setRazorPayPaymentMethod = useEventZStore((state) => state.setRazorPayPaymentMethod);
  const setActiveProductTabLanguages = useAppPersistenceStore(
    (state) => state.setActiveProductTabLanguages
  );
  const setAutoPlayBannerADEnabled = useEventZStore((state) => state.setAutoPlayBannerADEnabled);
  const setInitialEventParams = useEventStore.getState()?.setInitialEventParams;
  const initialEventParams = useEventStore.getState()?.initialEventParams;
  const setEnableAudioListenerPlatforms = useEventZStore(
    (state) => state.setEnableAudioListenerPlatforms
  );
  const setInitializeAdsShow = useEventZStore((state) => state.setInitializeAdsShow);


  const initializeEventSDK = () => {
    useEventStore.getState().setConfig({
      env: 'prod' as Env,
      postHogKey: initialBootstrapData?.POSTHOG_KEY ?? process.env.APP_POSTHOG_KEY,
      postHogUrl: initialBootstrapData?.POSTHOG_URL ?? process.env.APP_POSTHOG_DOMAIN,
      baseUrl: process.env.APP_EVENTS_URL!,
      topic: initialBootstrapData?.LAKEHOUSE_EVENT_TOPIC ?? process.env.APP_LAKEHOUSE_TOPIC,
      onLogError: getLogError,
      onLogMessage: getLogMessage,
    });
  };

  // Step 2: Initialize PostHog explicitly (IMPORTANT for monorepo!)
  const initializePostHog = () => {
    // const posthogInstance = setupPostHog({
    //   postHogKey: process.env.POSTHOG_KEY ||initialBootstrapData?.POSTHOG_KEY,
    //   postHogUrl: process.env.POSTHOG_URL || initialBootstrapData?.POSTHOG_URL,
    // });
    // // Verify initialization
    // if (isPostHogReady()) {
    //   console.log('✅ PostHog is ready to track events');
    //   return posthogInstance;
    // } else {
    //   console.error('❌ PostHog initialization failed');
    //   return null;
    // }
  };

  // Step 3: Complete initialization function
  const initializeSDK = () => {
    // First, set up event configuration
    initializeEventSDK();
    // Then, initialize PostHog
    const posthog = initializePostHog();
    // Return the PostHog instance for manual tracking if needed
    return posthog;
  };

  const initializeApp = async () => {
    try {
      const deviceUniqueId = await getUniqueDeviceId();
      await initializeDeviceDetails(deviceId ?? deviceUniqueId);
      await initializePlatformSpecificFeatures();
      setIsReady(true);
      applyBootstrapConfig();
    } catch (error) {
      console.error('Error initializing app:', error);
      if (onError) onError(error as Error);
    }
  };

  const captureTraffic = async () => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const browserName = browser.getBrowserName();
    const urlParams = new URLSearchParams(window.location.search);

    // Default values
    let utm_medium = 'direct';
    let utm_source = 'direct';
    let utm_campaign = '';

    // Try to read utms (base64 or normal)
    const base64Utms = urlParams.get('utms');
    const utm_medium_param = urlParams.get('utm_medium');

    let searchParams: URLSearchParams | null = null;

    if (base64Utms) {
      try {
        searchParams = new URLSearchParams(atob(base64Utms));
      } catch {
        searchParams = null;
      }
    } else if (utm_medium_param) {
      searchParams = urlParams;
    }

    if (!searchParams) {
      console.log('No UTM or utms in URL - skip updating eventDetails');
      return;
    }

    // Extract values with fallback
    utm_medium = searchParams.get('utm_medium') || 'direct';
    utm_source = searchParams.get('utm_source') || 'direct';
    utm_campaign = searchParams.get('utm_campaign') || '';

    const trafficSource = { browser: browserName, medium: utm_medium };
    const collectedTrafficSource = {
      manualCampaignId: '',
      manualCampaignName: utm_campaign,
      manualMedium: utm_medium,
      manualSource: utm_source,
      manualTerm: '',
    };

    // Update eventDetails (merge objects to avoid repetition)
    setEventDetails({ ...collectedTrafficSource, browser: browserName, medium: utm_medium });

    // Save to IndexedDB
    await saveToIndexedDB('trafficSource', {
      id: 'trafficSource',
      trafficSource,
      collectedTrafficSource,
    });
  };

  const nativeSideRenderingFunction = async (deviceId, setReferrerInfo, appLanguage, enqueue) => {
    setupNativeBootstrap();
    initializeClarity();
    initializeFacebookSDK(deviceId);
    checkInstallReferrer(setReferrerInfo, appLanguage, enqueue);
  };

  const initializeNotification = async () => {
    await requestNotificationPermission();
  };

  useEffect(() => {
    useFF4JStore.getState().reset();
    initializeSDK();
    initializeApp();
    if (Platform.OS == 'web') {
      captureTraffic();
    } else {
      initializeNotification();
      nativeSideRenderingFunction(deviceId, setReferrerInfo, appLanguage ?? 'hi', enqueue);
    }

    if (isWeb) {
      localStorage.removeItem('plyr');
    }
  }, []);

  // Initialize IP location
  useEffect(() => {
    if (iplocationDetailsRef?.current) {
      setIpAddressLocation(iplocationDetailsRef?.current);
      handleLanguageDetection(iplocationDetailsRef?.current);
    }
  }, [iplocationDetailsRef?.current]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      ReactNativeIdfaAaid.getAdvertisingInfo()
        .then((res) => {
          setIsAdTrackingLimited(res?.isAdTrackingLimited);
          setAdId(!res.isAdTrackingLimited ? res?.id : null);
        })
        .catch((err) => {
          console.log(err);
          setAdId(null);
        });
    }
  }, [setIsAdTrackingLimited, setAdId]);

  const applyBootstrapConfig = () => {
    setAndroidPlatform(initialBootstrapData?.PLATFORM_ANDROID);
    setIOSPlatform(initialBootstrapData?.PLATFORM_IOS);
    setTrueCallerEnabled(initialBootstrapData?.TRUECALLER_AUTH_ENABLED);
    setGlobalSearchEnabled(initialBootstrapData?.ENABLE_GLOBAL_SEARCH);
    setMandiNewsEnabled(initialBootstrapData?.ENABLE_MANDI_NEWS);
    setNewUserLoginRedirectPath(initialBootstrapData?.NEW_USER_LOGIN_REDIRECT_PATH);
    setHomeHeaderEnabled(initialBootstrapData?.ENABLE_HOME_CTA);
    setPlayListTitle(initialBootstrapData?.DEEPLINK_PLAYLIST_TITLE);
    setLanguages(initialBootstrapData?.LANGUAGES);
    setAutoVideoPlayer(initialBootstrapData?.ENABLE_VIDEO_AUTO_PLAY ?? true);
    setIsVideoplayerAdEnable(initialBootstrapData?.VIDEO_PLAYER_AD_ENABLED);
    setContactUsWhatsappNumber(initialBootstrapData?.CONTACT_US_WHATSAPP_NUMBER ?? '+919886999284');
    setBannerAdKeywords(initialBootstrapData?.ADS_KEYWORDS);
    // setSwipeAnimation(initialBootstrapData?.SWIPE_ANIMATION);
    setShortsAdFrequency(initialBootstrapData?.SHORTS_AD_FREQUENCY);
    setShortsAdId(initialBootstrapData?.SHORTS_AD_ID ?? adUnits.shortsUnitId);
    setShowHotFixPopup(initialBootstrapData?.SHOW_HOTFIX_POPUP ?? false);
    setIsActiveTourGuides(initialBootstrapData?.IS_ACTIVE_TOUR_GUIDES);
    setAdSizes(initialBootstrapData?.BANNER_AD_UNITS?.adUnitsWithSizes);
    setAdSizeWeightage(initialBootstrapData?.BANNER_AD_UNITS?.adaptive ?? false);
    setWeatherUnitId(initialBootstrapData?.WEATHER_AD_UNITID);
    setAdTimerShow(initialBootstrapData?.AD_SHOWN_TIME ?? 30);
    setProfileTabLanguageFilter(initialBootstrapData?.PROFILE_TAB_LANGUAGE_FILTER ?? ['hi', 'en']);
    setEnableLanguageIcon(initialBootstrapData?.ENABLE_HOME_HEADER_LANGUAGE_ICON);
    setHomeCustomTabEnabled(initialBootstrapData?.ENABLE_HOME_CUSTOM_TAB);
    setRazorPayPaymentMethod(initialBootstrapData?.RAZORPAY_PAYMENT_METHOD);
    setHomeHeaderEnabled(initialBootstrapData?.ENABLE_HOME_TAB_CTA);
    setEnableGlobalTabMic(initialBootstrapData?.ENABLE_GLOBAL_TAB_MIC ?? false);
    setEnableGlobalProfileTabMic(initialBootstrapData?.ENABLE_GLOBAL_PROFILE_TAB_MIC ?? false);
    setActiveProductTabLanguages(
      initialBootstrapData?.ACTIVE_PRODUCT_TAB_LANGUAGES ?? ['hi', 'en']
    );
    setAutoPlayBannerADEnabled(initialBootstrapData?.AUTOPLAY_BANNER_AD_ENABLED ?? true);
    setEnableAudioListenerPlatforms(
      initialBootstrapData?.ENABLE_AUDIO_LISTENER_ICON ?? ['web', 'android', 'ios']
    );
    setInitializeAdsShow(initialBootstrapData?.insterstitial_ad_on_launch ?? true)
  };

  const handleLanguageDetection = (ipLocation: any) => {
    const languageData = languageStateCode.filter(
      (el) => el.Code == ipLocation?.data?.stateCode
    )[0];

    // setCookie('lang', languageData?.Language, 1500);

    const languageExists = appLanguage;
    console.log('🚀 ~ handleLanguageDetection ~ languageExists:', languageExists);
    // console.log('Hnadling language detection!!!', languageData?.Language);
    if (languageExists) return;
    setAppLanguage(languageData?.Language ?? 'hi');

    if (languageData?.Language) {
      setAppLanguageLabel(languageData.LanguageName);
      setEdTechAppLanguageLabel(languageData.LanguageName);
      changeLanguage(languageData?.Language).then(async () => {
        setAppLanguage(languageData?.Language);
      });
    }
    if (Platform.OS !== 'web') {
      setFirstLaunchTime(new Date().getTime());
    }
  };

  const getUniqueDeviceId = async (): Promise<string> => {
    if (Platform.OS === 'web') {
      const eventInMemoryStore = EventInMemoryStore.getInstance();
      const savedData = localStorage.getItem('inMemoryStore');
      if (savedData) {
        eventInMemoryStore.hydrate(JSON.parse(savedData));
      }
      return getUserDeviceId();
    } else {
      return '';
    }
  };

  const getLogError = (error: any) => {
    crashlytics().recordError(error);
  };

  const getLogMessage = (message) => {
    crashlytics().log(`${message}`);
  };

  const initializeDeviceDetails = async (deviceId: string) => {
    try {
      setDeviceId(deviceId);
      const fingerprintDeviceId = Platform.OS === 'web' ? await getUserDeviceIdFingerprint() : null;
      const profileStore = useProfileStore.getState();
      // console.log('🚀 ~ initializeDeviceDetails ~ profileStore:', profileStore);

      setInitialEventParams({
        appLanguage,
        uniqueDeviceId: deviceId,
        profileStore: profileStore,
        initialIPLocation: iplocationDetailsRef.current,
        fingerprintjsId: fingerprintDeviceId!,
      });

      const deviceDetails = initialEventParams?.profileStore?.userProfile;
      const userId = userProfile?.user?.id ? userProfile?.user?.id : deviceId;
      if (deviceDetails) {
        // Use it to set eventDetails
        setEventDetails(deviceDetails);
        if (typeof window !== 'undefined' && Platform.OS == 'web') {
          await saveToIndexedDB('deviceDetails', {
            id: 'deviceDetails',
            ...deviceDetails,
          });
        }
      }
    } catch (error) {
      console.error('Error initializing device details:', error);
      throw error;
    }
  };

  const setCookie = (name: string, value: string, days: number): void => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000); // Convert days to milliseconds
    const expires = 'expires=' + date.toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; Secure; SameSite=Lax`;
  };

  async function saveToIndexedDB(storeName: any, data: any) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('kisan', 1);

      request.onerror = (event) => {
        console.error('Error opening IndexedDB:', event);
        reject(event);
      };

      request.onsuccess = (event: any) => {
        const db = event.target.result;
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const addRequest = store.put(data);

        addRequest.onerror = (event: any) => {
          console.error('Error adding data to IndexedDB:', event);
          reject(event);
        };
        addRequest.onsuccess = (event: any) => {
          resolve(event);
        };
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('deviceDetails')) {
          db.createObjectStore('deviceDetails', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('trafficSource')) {
          db.createObjectStore('trafficSource', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('ipAddressLocation')) {
          db.createObjectStore('ipAddressLocation', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('bootstrapData')) {
          db.createObjectStore('bootstrapData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('userDetails')) {
          db.createObjectStore('userDetails', { keyPath: 'id' });
        }
      };
    });
  }

  const initializePlatformSpecificFeatures = async () => {
    try {
      if (Platform.OS === 'web') {
        // await setupWebBootstrap();
      } else {
        setAppStartingTime(startTimeRef.current);
        logEvent('BOOTSTRAPING_DATA', {
          appLaunchStatus: true,
        });
        logEvent('IP_BASED_LOCATION', {
          appLaunchStatus: true,
        });
        await checkAndRenewStreamToken();
        setPostHogUserOrDeviceId();
      }
    } catch (error) {
      console.error('Error initializing platform features:', error);
      throw error;
    }
  };

  const checkAndRenewStreamToken = async () => {
    try {
      await getStreamUserDetails();
    } catch (error) {
      console.error('Error renewing stream token:', error);
      throw error;
    }
  };

  const setPostHogUserOrDeviceId = () => {
    logEvent('AppLanchEvent', {
      appLaunchStatus: true,
    });
    const userProfile = useProfileStore.getState()?.userProfile;
    const uniqueDeviceId = useAppPersistenceStore.getState()?.deviceId;

    const postHogAttributionId = getDistinctId;
    posthog?.identify(postHogAttributionId?.() ?? uniqueDeviceId! ?? -1, {
      userId: userProfile?.user?.id ?? -1,
      systemUserId: userProfile?.user?.id ?? uniqueDeviceId ?? -1,
      deviceId: uniqueDeviceId!,
      name: userProfile?.user?.name ?? userProfile?.user?.id ?? postHogAttributionId?.(),
    });
  };

  return <>{children}</>;
};
